import { useCallback, useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../context/WalletContext';
import { useContracts } from './useContract';

const BN0 = ethers.BigNumber.from(0);

/**
 * Per-user pool status:
 *   - currentPool (0=none, 1=X1, 2=X2, 3=X3)
 *   - claimableUSDT — how much they can claim right now
 *   - unclaimedRounds — number of distribution rounds waiting
 *   - snapshot — eligibility breakdown (selfInvestment, legs, etc.)
 *
 * Returns { available: false } if XterraPools isn't deployed yet.
 */
export function usePoolStatus(userId) {
  const { account, isCorrectNetwork } = useWallet();
  const { read } = useContracts();

  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(!!read.pools);
  const [data, setData] = useState({
    currentPool: 0,
    claimableUSDT: BN0,
    unclaimedRounds: 0,
    snapshot: null,
  });

  const refresh = useCallback(async () => {
    if (!read.pools || !userId) {
      setData({ currentPool: 0, claimableUSDT: BN0, unclaimedRounds: 0, snapshot: null });
      setAvailable(!!read.pools);
      return;
    }
    setLoading(true);
    setAvailable(true);
    try {
      const [preview, snapshot] = await Promise.all([
        read.pools.previewClaim(userId).catch(() => null),
        read.pools.getEligibilitySnapshot(userId).catch(() => null),
      ]);
      setData({
        currentPool: preview ? Number(preview.currentPool) : 0,
        claimableUSDT: preview ? preview.claimableUSDT : BN0,
        unclaimedRounds: preview ? Number(preview.unclaimedRounds) : 0,
        snapshot,
      });
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [read.pools, userId]);

  useEffect(() => {
    if (account && isCorrectNetwork && userId) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, isCorrectNetwork, userId]);

  return { loading, available, ...data, refresh };
}
